<template>
  <div class="my-phone-input">
    <label class="my-phone-input__label" :for="id" v-if="label">
      {{ label }}
    </label>
    <div class="my-phone-input__container">
      <!-- Country Dropdown -->
      <div class="my-phone-input__country-selector">
        <button
          type="button"
          class="my-phone-input__country-button"
          @click="toggleCountryDropdown"
          ref="countryButton"
        >
          <span class="my-phone-input__country-flag">{{ selectedCountryFlag }}</span>
          <svg
            class="my-phone-input__dropdown-icon"
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          v-show="showCountryDropdown"
          class="my-phone-input__country-dropdown"
          ref="countryDropdown"
        >
          <div class="my-phone-input__country-search">
            <input
              type="text"
              v-model="countrySearch"
              placeholder="Search countries..."
              class="my-phone-input__search-input"
              @keydown.escape="closeCountryDropdown"
            />
          </div>
          <div class="my-phone-input__country-list">
            <button
              type="button"
              v-for="country in filteredCountries"
              :key="country.code"
              class="my-phone-input__country-option"
              :class="{
                'my-phone-input__country-option--selected': country.code === selectedCountry,
              }"
              @click="selectCountry(country)"
            >
              <span class="my-phone-input__country-flag">{{ country.flag }}</span>
              <span class="my-phone-input__country-name">{{ country.name }}</span>
              <span class="my-phone-input__country-code">+{{ country.callingCode }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Phone Number Input -->
      <input
        :id="id"
        type="tel"
        :placeholder="placeholder"
        :value="displayValue"
        @input="onInput"
        @cut="onCut"
        @keydown="onKeyDown"
        @blur="onBlur"
        @focus="onFocus"
        class="my-phone-input__input"
      />

      <!-- Twilio Validation Indicator -->
      <div
        v-if="enableTwilioValidation && validationIndicatorState"
        class="my-phone-input__validation-indicator"
      >
        <!-- Show loading spinner when validating -->
        <div v-if="validationIndicatorState === 'loading'" class="my-phone-input__loading">
          <div class="my-phone-input__spinner"></div>
        </div>
        <!-- Show success only when BOTH format is valid AND Twilio validation passed -->
        <div v-else-if="validationIndicatorState === 'success'" class="my-phone-input__success">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M13.5 4.5L6 12L2.5 8.5"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <!-- Show error if format is invalid OR Twilio validation failed -->
        <div v-else-if="validationIndicatorState === 'error'" class="my-phone-input__error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Enhanced Phone Input Component with Integrated Country Code Display
 *
 * This component provides an intuitive phone number input experience with:
 *
 * KEY FEATURES:
 * 1. Integrated Country Code Display: Country code (+1, +44, etc.) is always shown
 *    within the input field itself, not in the dropdown button
 * 2. Smart Country Detection: Automatically detects and switches country when user
 *    enters international format (+44 20...)
 * 3. Two-Stage Validation System:
 *    - Stage 1: libphonenumber-js for syntax/format validation
 *    - Stage 2: Twilio Lookup API for number existence verification
 *
 * UI BEHAVIOR:
 * - Dropdown Button: Shows only country flag (🇺🇸) and dropdown arrow
 * - Input Field: Always displays country code as part of the value:
 *   • Empty state: "+1 " (cursor positioned after space)
 *   • Local number: "+1 (555) 123-4567"
 *   • International: "+44 20 1234 5678" (auto-switches to UK flag)
 * - Country code cannot be deleted by user - it's protected
 *
 * VALIDATION FLOW:
 * 1. User types number → libphonenumber-js validates format
 * 2. If format valid → Twilio API validates number existence
 * 3. Visual indicators show loading/success/error states
 *
 * EVENTS EMITTED:
 * - @input: Phone number value (in E.164 format when possible)
 * - @validate: Detailed validation state object
 * - @country-changed: When country is auto-detected or manually selected
 * - @focus/@blur: Standard input events
 */

import { nanoid } from "nanoid";
import {
  AsYouType,
  parsePhoneNumber,
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import axios from "axios";
import debounce from "lodash/debounce";
import urls from "@/constants/urls";
import { countryList } from "@/constants/countries";
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      id: "",
      formattedValue: "",
      asYouType: null,
      showCountryDropdown: false,
      countrySearch: "",
      selectedCountry: "US", // Default country
      twilioValidationState: {
        isValidating: false,
        isValid: null,
        hasError: false,
      },
      // Initialize debounced function in data to ensure it's available immediately
      debouncedTwilioValidation: null,
    };
  },

  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Enter phone number",
    },
    defaultCountry: {
      type: String,
      default: "US",
    },
    enableTwilioValidation: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    availableCountries() {
      // Filter countries to only include those supported by libphonenumber-js
      const supportedCountries = getCountries();
      return countryList
        .filter((country) => supportedCountries.includes(country.iso_code))
        .map((country) => ({
          code: country.iso_code,
          name: country.name,
          flag: country.flag,
          callingCode: getCountryCallingCode(country.iso_code),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    filteredCountries() {
      if (!this.countrySearch) {
        return this.availableCountries;
      }
      const search = this.countrySearch.toLowerCase();
      return this.availableCountries.filter(
        (country) =>
          country.name.toLowerCase().includes(search) ||
          country.code.toLowerCase().includes(search) ||
          country.callingCode.includes(search)
      );
    },

    selectedCountryData() {
      return this.availableCountries.find((country) => country.code === this.selectedCountry);
    },

    selectedCountryFlag() {
      return this.selectedCountryData?.flag || "🇺🇸";
    },

    selectedCountryCode() {
      return this.selectedCountryData?.callingCode || "1";
    },

    isValid() {
      if (!this.value) return true; // Empty is considered valid
      try {
        return isValidPhoneNumber(this.value, this.selectedCountry);
      } catch {
        return false;
      }
    },

    parsedPhoneNumber() {
      if (!this.value) return null;
      try {
        return parsePhoneNumber(this.value, this.selectedCountry);
      } catch {
        return null;
      }
    },

    // Get the international format phone number for validation
    internationalPhoneNumber() {
      if (!this.value) return "";
      try {
        const parsed = parsePhoneNumber(this.value, this.selectedCountry);
        return parsed ? parsed.format("E.164") : this.value;
      } catch {
        return this.value;
      }
    },

    // Overall validation state for visual indicators
    validationIndicatorState() {
      if (!this.value) return null;

      if (this.twilioValidationState.isValidating) {
        return "loading";
      }

      // Both validations must pass for success
      if (this.isValid && this.twilioValidationState.isValid) {
        return "success";
      }

      // Show error if format is invalid OR Twilio validation failed
      if (!this.isValid || !this.twilioValidationState.isValid) {
        return "error";
      }

      // No indicator if we haven't validated yet
      return null;
    },

    isInternationalFormat() {
      return this.value && this.value.startsWith("+");
    },

    // Computed property for what to display in the input field
    displayValue() {
      // Always show country code prefix for non-international numbers
      if (!this.formattedValue && !this.value) {
        // Show just the country code when empty
        return `+${this.selectedCountryCode} `;
      }

      // If the formatted value already starts with +, show it as is
      if (this.formattedValue.startsWith("+")) {
        return this.formattedValue;
      }

      // If we have a value but it's not international format, prepend the country code
      if (this.formattedValue && !this.value?.startsWith("+")) {
        return `+${this.selectedCountryCode} ${this.formattedValue}`;
      }

      return this.formattedValue;
    },
  },

  watch: {
    value: {
      handler(newValue) {
        if (newValue !== this.formattedValue) {
          // Use nextTick to ensure all reactive updates are processed
          this.$nextTick(() => {
            this.updateFormattedValue(newValue);
          });
        }
      },
      immediate: true,
    },

    selectedCountry() {
      this.initializeAsYouType();
      // Only reformat if we have a value and it's not already in international format
      if (this.value && !this.value.startsWith("+")) {
        this.updateFormattedValue(this.value);
      }
      // Force Vue to update the UI
      this.$forceUpdate();
    },

    defaultCountry: {
      handler(newCountry) {
        if (newCountry && this.availableCountries.find((c) => c.code === newCountry)) {
          this.selectedCountry = newCountry;
        }
      },
      immediate: true,
    },

    // Watch for changes in validity to trigger Twilio validation
    isValid(newIsValid, oldIsValid) {
      if (this.enableTwilioValidation && this.value && newIsValid && !oldIsValid) {
        // Phone just became valid, trigger Twilio validation
        const internationalNumber = this.internationalPhoneNumber;
        this.debouncedTwilioValidation(internationalNumber);
      }
    },
  },

  created() {
    // Initialize Twilio validation with debounce early
    this.debouncedTwilioValidation = debounce(this.validateWithTwilio, 1000);
  },

  mounted() {
    this.id = nanoid();
    this.initializeAsYouType();

    // Close dropdown when clicking outside
    document.addEventListener("click", this.handleOutsideClick);

    // If we have an initial value, trigger validation after mount
    if (this.value && this.isValid && this.enableTwilioValidation) {
      this.$nextTick(() => {
        const internationalNumber = this.internationalPhoneNumber;
        // Use immediate validation for initial values
        this.validateWithTwilio(internationalNumber);
      });
    }
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    // Cancel any pending debounced calls
    if (this.debouncedTwilioValidation && this.debouncedTwilioValidation.cancel) {
      this.debouncedTwilioValidation.cancel();
    }
  },

  methods: {
    /**
     * Initialize the AsYouType formatter for the current country
     */
    initializeAsYouType() {
      this.asYouType = new AsYouType(this.selectedCountry);
    },

    /**
     * Update the formatted display value
     * @param {string} value - The phone number value to format
     */
    updateFormattedValue(value) {
      if (!value) {
        this.formattedValue = "";
        // Reset validation state when value is cleared
        this.twilioValidationState = {
          isValidating: false,
          isValid: null,
          hasError: false,
        };
        this.emitValidation();
        return;
      }

      // Ensure asYouType is initialized
      if (!this.asYouType) {
        this.initializeAsYouType();
      }

      // Process the phone number (country detection, formatting, validation)
      this.processPhoneNumber(value, false); // false = not from user input
    },

    /**
     * Process phone number input with auto-detection, formatting and validation
     * @param {string} input - Raw input from user or initial value
     * @param {boolean} isUserInput - Whether this came from user typing (vs programmatic)
     */
    processPhoneNumber(input, isUserInput = true) {
      // Handle empty input
      if (!input || input.trim() === "") {
        this.formattedValue = "";
        if (isUserInput) {
          this.$emit("input", "");
        }
        // Reset validation state
        this.twilioValidationState = {
          isValidating: false,
          isValid: null,
          hasError: false,
        };
        this.emitValidation();
        return;
      }

      // Ensure asYouType is initialized
      if (!this.asYouType) {
        this.initializeAsYouType();
      }

      let detectedCountry = null;
      let normalizedInput = input;

      // Auto-detect country from international numbers
      if (input.startsWith("+") && input.length > 3) {
        try {
          const parsed = parsePhoneNumber(input);
          if (parsed && parsed.country) {
            // Check if the detected country is in our available countries
            const foundCountry = this.availableCountries.find((c) => c.code === parsed.country);
            if (foundCountry) {
              detectedCountry = foundCountry;
            }
          }
        } catch (error) {
          // Ignore parsing errors for incomplete numbers
        }
      } else if ((input.startsWith("00") || input.startsWith("011")) && input.length > 5) {
        // Handle common international dialing prefixes
        try {
          // Remove the international dialing prefix and add +
          if (input.startsWith("011")) {
            normalizedInput = "+" + input.substring(3);
          } else if (input.startsWith("00")) {
            normalizedInput = "+" + input.substring(2);
          }

          const parsed = parsePhoneNumber(normalizedInput);
          if (parsed && parsed.country) {
            const foundCountry = this.availableCountries.find((c) => c.code === parsed.country);
            if (foundCountry) {
              detectedCountry = foundCountry;
            }
          }
        } catch (error) {
          // Ignore parsing errors for incomplete numbers
        }
      }

      // Update country if detected
      if (detectedCountry) {
        this.selectedCountry = detectedCountry.code;
        this.asYouType = new AsYouType(this.selectedCountry);
        this.$emit("country-changed", detectedCountry);
      }

      // Format the phone number
      this.asYouType.reset();
      const formatted = this.asYouType.input(normalizedInput);
      this.formattedValue = formatted;

      // Get the actual phone number
      const phoneNumber = this.asYouType.getNumber();
      const phoneValue = phoneNumber ? phoneNumber.number : normalizedInput;

      // Only emit input event if this is from user input
      if (isUserInput) {
        this.$emit("input", phoneValue);
      }

      // Handle validation
      this.handleValidation(phoneValue);
    },

    handleValidation(phoneValue) {
      // Two-stage validation process:
      // 1. libphonenumber-js validates syntax/format
      // 2. Twilio validates if the number actually exists/is reachable
      if (this.enableTwilioValidation && phoneValue) {
        // Check if the phone number format is valid
        const isFormatValid = this.isValid;

        if (isFormatValid) {
          // Stage 1 passed: Number has valid syntax, now check with Twilio
          // Use international format for Twilio validation
          const internationalNumber = this.internationalPhoneNumber;

          // Ensure debouncedTwilioValidation is available
          if (this.debouncedTwilioValidation) {
            this.debouncedTwilioValidation(internationalNumber);
          }
        } else {
          // Stage 1 failed: Invalid syntax, skip Twilio validation
          // Cancel any pending Twilio validation
          if (this.debouncedTwilioValidation && this.debouncedTwilioValidation.cancel) {
            this.debouncedTwilioValidation.cancel();
          }
          // Reset Twilio validation state when format is invalid
          this.twilioValidationState = {
            isValidating: false,
            isValid: null,
            hasError: false,
          };
          this.emitValidation();
        }
      } else {
        // Twilio validation disabled or no phone number
        // Cancel any pending Twilio validation
        if (this.debouncedTwilioValidation && this.debouncedTwilioValidation.cancel) {
          this.debouncedTwilioValidation.cancel();
        }
        // Reset validation state
        this.twilioValidationState = {
          isValidating: false,
          isValid: null,
          hasError: false,
        };
        this.emitValidation();
      }
    },

    /**
     * Handle user input in the phone number field
     * Extracts number part when country code is displayed in input
     * @param {Event} event - Input event
     */
    onInput(event) {
      let input = event.target.value;

      // If we're showing the country code in the input and the user hasn't entered +
      if (!this.isInternationalFormat && !input.startsWith("+")) {
        // If user deleted everything including country code, restore it
        if (input.trim() === "") {
          event.target.value = `+${this.selectedCountryCode} `;
          return;
        }
      }

      // Handle when country code is shown in input
      if (!this.isInternationalFormat && input.startsWith(`+${this.selectedCountryCode}`)) {
        // Extract just the phone number part
        const prefix = `+${this.selectedCountryCode} `;
        if (input.startsWith(prefix)) {
          input = input.substring(prefix.length);
        } else if (input === `+${this.selectedCountryCode}`) {
          // User deleted the space after country code
          input = "";
        }
      }

      this.processPhoneNumber(input, true); // true = from user input
    },

    onCut(event) {
      // Handle cut event (Ctrl+X)
      this.$nextTick(() => {
        const input = event.target.value;
        this.processPhoneNumber(input || "", true);
      });
    },

    onKeyDown(event) {
      const input = event.target;
      const prefix = `+${this.selectedCountryCode} `;

      // Prevent deleting the country code prefix
      if (!this.isInternationalFormat && !this.value) {
        if (event.key === "Backspace" || event.key === "Delete") {
          if (input.selectionStart <= prefix.length && input.value.startsWith(prefix)) {
            event.preventDefault();
            // Position cursor after the prefix
            input.setSelectionRange(prefix.length, prefix.length);
            return;
          }
        }
      }

      // Handle delete/backspace when all text is selected
      if (
        (event.key === "Delete" || event.key === "Backspace") &&
        event.target.selectionStart === 0 &&
        event.target.selectionEnd === event.target.value.length
      ) {
        this.$nextTick(() => {
          if (!event.target.value || event.target.value === prefix) {
            // Restore country code prefix
            event.target.value = prefix;
            event.target.setSelectionRange(prefix.length, prefix.length);
            this.processPhoneNumber("", true);
          }
        });
      }
    },

    onBlur() {
      // Force immediate validation on blur if we have a valid phone number
      if (this.enableTwilioValidation && this.value && this.isValid) {
        // Cancel any pending debounced call
        if (this.debouncedTwilioValidation && this.debouncedTwilioValidation.cancel) {
          this.debouncedTwilioValidation.cancel();
        }
        // Run validation immediately with international format
        const internationalNumber = this.internationalPhoneNumber;
        this.validateWithTwilio(internationalNumber);
      } else {
        this.emitValidation();
      }
      this.$emit("blur");
    },

    onFocus(event) {
      // If the input only contains the country code, position cursor at the end
      if (!this.value && !this.isInternationalFormat) {
        this.$nextTick(() => {
          const input = event.target;
          const prefix = `+${this.selectedCountryCode} `;
          if (input.value === prefix) {
            input.setSelectionRange(prefix.length, prefix.length);
          }
        });
      }
      this.$emit("focus");
    },

    toggleCountryDropdown() {
      this.showCountryDropdown = !this.showCountryDropdown;
      if (this.showCountryDropdown) {
        this.countrySearch = "";
        this.$nextTick(() => {
          const searchInput = this.$refs.countryDropdown?.querySelector(
            ".my-phone-input__search-input"
          );
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    },

    closeCountryDropdown() {
      this.showCountryDropdown = false;
      this.countrySearch = "";
    },

    selectCountry(country) {
      this.selectedCountry = country.code;
      this.closeCountryDropdown();
      this.$emit("country-changed", country);
    },

    handleOutsideClick(event) {
      if (
        !this.$refs.countryButton?.contains(event.target) &&
        !this.$refs.countryDropdown?.contains(event.target)
      ) {
        this.closeCountryDropdown();
      }
    },

    /**
     * Validate phone number using Twilio Lookup API
     * @param {string} phoneNumber - Phone number in E.164 format
     */
    async validateWithTwilio(phoneNumber) {
      if (!this.enableTwilioValidation || !phoneNumber || !phoneNumber.trim()) {
        this.twilioValidationState = {
          isValidating: false,
          isValid: null,
          hasError: false,
        };
        this.emitValidation();
        return;
      }

      this.twilioValidationState.isValidating = true;
      this.emitValidation();

      try {
        await axios.get(
          urls.twilioLookup.replace(":phone", encodeURIComponent(phoneNumber.trim()))
        );

        this.twilioValidationState = {
          isValidating: false,
          isValid: true,
          hasError: false,
        };
      } catch (error) {
        console.error("Twilio API validation error:", error.response?.data || error.message);
        this.twilioValidationState = {
          isValidating: false,
          isValid: false,
          hasError: true,
        };
      }

      this.emitValidation();
    },

    /**
     * Emit validation state to parent component
     */
    emitValidation() {
      this.$emit("validate", {
        // Stage 1: libphonenumber-js syntax validation
        isValidFormat: this.isValid,
        // Stage 2: Twilio reachability validation
        twilioValidation: {
          ...this.twilioValidationState,
        },
        // Overall validity: both stages must pass
        // Valid when format is valid AND (Twilio hasn't checked yet OR Twilio passed)
        isValid:
          this.isValid &&
          (this.twilioValidationState.isValid === null || this.twilioValidationState.isValid),
        phoneNumber: this.parsedPhoneNumber,
        country: this.selectedCountry,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.my-phone-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid #969696;
  padding-bottom: 3px;
  position: relative;
  direction: ltr;

  &__label {
    margin-bottom: 0;
    font-size: 14px;
    color: var(--my-text-color);
  }

  &__container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__country-selector {
    position: relative;
    flex-shrink: 0;
  }

  &__country-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: var(--my-primary);
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--my-white-soft, #f8f9fa);
    }

    &:focus {
      outline: none;
      border-color: var(--my-primary);
    }
  }

  &__country-flag {
    font-size: 16px;
    line-height: 1;
  }

  &__country-code {
    font-size: 16px;
    font-weight: 600;
    min-width: 24px;
  }

  &__dropdown-icon {
    color: var(--my-text-color);
    transition: transform 0.2s;

    .my-phone-input__country-button:hover & {
      color: var(--my-primary);
    }
  }

  &__country-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    min-width: 280px;
    max-height: 300px;
    background: var(--my-white, #ffffff);
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  &__country-search {
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  &__search-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 14px;
    outline: none;

    &:focus {
      border-color: var(--my-primary);
    }
  }

  &__country-list {
    max-height: 200px;
    overflow-y: auto;
  }

  &__country-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--my-white-soft, #f8f9fa);
    }

    &--selected {
      background-color: var(--my-primary);
      color: white;
    }
  }

  &__country-name {
    flex: 1;
    font-weight: 400;
  }

  &__input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 18px;
    font-weight: 600;
    line-height: inherit;
    color: var(--my-primary);
    outline: none;
    padding: 0;

    &::placeholder {
      color: var(--my-text-color);
      font-weight: 400;
    }
  }

  &__validation-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--my-primary, #007bff);
    border-radius: 50%;
    border-top-color: transparent;
    animation: spin 1s linear infinite;
  }

  &__success {
    width: 16px;
    height: 16px;
    color: #28a745;
    flex-shrink: 0;
  }

  &__error {
    width: 16px;
    height: 16px;
    color: #dc3545;
    flex-shrink: 0;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}

// Error state styles
.error-field .my-phone-input {
  border-bottom-color: #ef4444 !important;

  .my-phone-input__input {
    color: #ef4444 !important;
  }

  .my-phone-input__label {
    color: #ef4444 !important;
  }

  .my-phone-input__country-code {
    color: #ef4444 !important;
  }
}
</style>
